import React, { useState, useEffect } from 'react';
import './popup.scss';
import editImage from '../../../../assets/images/edit.svg';
import { SelectField } from '../../../atom/SelectField';
import { TextField } from '../../../atom/TextField';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import { postWithToken } from '../../../../services/apiRequest';
import { showToast } from '../../../atom/toaster/toaster';

const ProductLinePopup = ({ isOpen, selectedRow, onClose, onOverlayClick, products = [], sectionOptions = [], onUpdate, rowType = 'task' }) => {
  const { access_token } = useAuthContext();
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingProductLine, setEditingProductLine] = useState(null);
  const [formData, setFormData] = useState({
    product: '',
    rum: '',
    section: '',
    quantity: ''
  });
  const [errors, setErrors] = useState({
    product: '',
    rum: '',
    section: '',
    quantity: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [productLines, setProductLines] = useState([]);

  // Reset form when popup closes or when a different row is selected
  useEffect(() => {
    if (!isOpen) {
      // Reset form when popup closes
      setShowEditForm(false);
      setEditingProductLine(null);
      setSearchTerm('');
      setFormData({
        product: '',
        rum: '',
        section: '',
        quantity: ''
      });
      setErrors({
        product: '',
        rum: '',
        section: '',
        quantity: ''
      });
    } else if (isOpen && selectedRow) {
      // Reset form when popup opens with a new row
      setShowEditForm(false);
      setEditingProductLine(null);
      setSearchTerm('');
      setFormData({
        product: '',
        rum: '',
        section: '',
        quantity: ''
      });
      setErrors({
        product: '',
        rum: '',
        section: '',
        quantity: ''
      });
    }
  }, [isOpen, selectedRow?.id]); // Reset when isOpen changes or selectedRow.id changes

  // Extract ProductLine array from selectedRow and sync with local state
  useEffect(() => {
    if (isOpen && selectedRow) {
      const allProductLines = selectedRow.ProductLine || selectedRow.productLine || [];
      setProductLines(allProductLines);
    } else {
      setProductLines([]);
    }
  }, [isOpen, selectedRow]);
  
  // Filter product lines based on search term
  const filteredProductLines = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return productLines;
    }
    const searchLower = searchTerm.toLowerCase().trim();
    return productLines.filter(productLine => {
      const title = String(productLine.name || productLine.title || '').toLowerCase();
      const section = String(productLine.section || '').toLowerCase();
      return title.includes(searchLower) || section.includes(searchLower);
    });
  }, [productLines, searchTerm]);

  if (!isOpen || !selectedRow) {
    return null;
  }

  // Extract section_options from prop, selectedRow, or fallback to empty array
  // Priority: prop (if provided and has data) > selectedRow.section_options > selectedRow.sectionOptions > []
  let sectionOptionsData = [];
  
  // First check if sectionOptions prop is provided and has data
  if (sectionOptions && Array.isArray(sectionOptions) && sectionOptions.length > 0) {
    sectionOptionsData = sectionOptions;
  } 
  // Then check selectedRow
  else if (selectedRow) {
    sectionOptionsData = selectedRow.section_options || selectedRow.sectionOptions || [];
  }
  
  // Ensure it's an array
  if (!Array.isArray(sectionOptionsData)) {
    sectionOptionsData = [];
  }

  // Map products API format to SelectField options format
  const productOptions = Array.isArray(products) && products.length > 0
    ? [
        { value: '', label: 'Select a product' },
        ...products.map(product => ({
          value: String(product.id),
          label: product.name
        }))
      ]
    : [{ value: '', label: 'Select a product' }];
  
  // Map section_options API format to SelectField options format
  const sectionFieldOptions = Array.isArray(sectionOptionsData) && sectionOptionsData.length > 0
    ? [
        { value: '', label: 'Select Section' },
        ...sectionOptionsData.map(section => {
          // Handle both { id, value } format and other possible formats
          const id = section.id !== undefined ? section.id : section.value;
          const label = section.value !== undefined ? section.value : (section.label || section.name || String(id));
          return {
            value: String(id),
            label: label
          };
        })
      ]
    : [{ value: '', label: 'Select Section' }];
  
  // Debug logging
  console.log('ProductLinePopup - products prop:', products);
  console.log('ProductLinePopup - productOptions:', productOptions);
  console.log('ProductLinePopup - sectionOptions prop:', sectionOptions);
  console.log('ProductLinePopup - selectedRow:', selectedRow);
  console.log('ProductLinePopup - sectionOptionsData:', sectionOptionsData);
  console.log('ProductLinePopup - sectionFieldOptions:', sectionFieldOptions);
  console.log('ProductLinePopup - sectionFieldOptions length:', sectionFieldOptions.length);

  // Handle edit button click - prefill form with product line data
  const handleEditClick = (productLine) => {
    setEditingProductLine(productLine);
    setShowEditForm(true);
    
    // Clear any previous errors
    setErrors({
      product: '',
      rum: '',
      section: '',
      quantity: ''
    });
    
    // Find matching product by product ID (item_id)
    // productLine.product contains the product item_id
    const productId = productLine.product || productLine.product_id || null;
    const matchedProduct = productId 
      ? products.find(p => (p.item_id || p.id) === productId)
      : null;
    
    // Find matching section - need to match productLine.section with sectionOptionsData
    let sectionValue = '';
    if (productLine.section && sectionOptionsData.length > 0) {
      const sectionToMatch = productLine.section;
      
      // Try to find matching section in sectionOptionsData
      // Match by ID, value, or label
      const matchedSection = sectionOptionsData.find(section => {
        const sectionId = section.id !== undefined ? section.id : section.value;
        const sectionLabel = section.value !== undefined ? section.value : (section.label || section.name || String(sectionId));
        
        // Try matching by ID, value, or label
        return String(sectionId) === String(sectionToMatch) || 
               String(sectionLabel) === String(sectionToMatch) ||
               section.value === sectionToMatch ||
               section.id === sectionToMatch;
      });
      
      if (matchedSection) {
        // Get the section ID to use as the dropdown value
        const sectionId = matchedSection.id !== undefined ? matchedSection.id : matchedSection.value;
        sectionValue = String(sectionId);
      } else {
        // Fallback: try direct match with sectionToMatch as string
        sectionValue = String(sectionToMatch);
      }
    } else if (productLine.section) {
      // If no sectionOptionsData but productLine has section, use it directly
      sectionValue = String(productLine.section);
    }
    
    // Prefill form fields
    setFormData({
      product: matchedProduct ? String(matchedProduct.id) : '',
      rum: String(productLine.rum || productLine.Rum || ''),
      section: sectionValue,
      quantity: String(productLine.Quantity || productLine.quantity || productLine['Quantity'] || '')
    });
  };

  // Handle add button click - open form and reset
  const handleAddClick = () => {
    setEditingProductLine(null);
    setShowEditForm(true);
    // Reset form when opening
    setFormData({
      product: '',
      rum: '',
      section: '',
      quantity: ''
    });
    setErrors({
      product: '',
      rum: '',
      section: '',
      quantity: ''
    });
  };

  // Handle close form button click
  const handleCloseForm = () => {
    setShowEditForm(false);
    setEditingProductLine(null);
    setFormData({
      product: '',
      rum: '',
      section: '',
      quantity: ''
    });
    setErrors({
      product: '',
      rum: '',
      section: '',
      quantity: ''
    });
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.stopPropagation();
    
    // Validate form fields
    const newErrors = {
      product: '',
      rum: '',
      section: '',
      quantity: ''
    };
    
    let hasErrors = false;
    
    // Validate Product
    if (!formData.product || formData.product === '') {
      newErrors.product = 'Product is required';
      hasErrors = true;
    }
    
    // Validate Rum
    const rumValue = String(formData.rum || '').trim();
    if (!rumValue) {
      newErrors.rum = 'Rum is required';
      hasErrors = true;
    }
    
    // Validate Section
    if (!formData.section || formData.section === '') {
      newErrors.section = 'Section is required';
      hasErrors = true;
    }
    
    // Validate Quantity
    const quantityValue = String(formData.quantity || '').trim();
    if (!quantityValue) {
      newErrors.quantity = 'Quantity is required';
      hasErrors = true;
    }
    
    // Set errors and return if validation fails
    if (hasErrors) {
      setErrors(newErrors);
      return;
    }
    
    // Clear errors if validation passes
    setErrors({
      product: '',
      rum: '',
      section: '',
      quantity: ''
    });
    
    // Check if we're adding a new product line or editing an existing one
    const isEditing = !!editingProductLine;

    // Get selected product details
    const selectedProduct = products.find(p => String(p.id) === formData.product);
    // Get product name from the selected product in products array
    const productTitle = selectedProduct?.name || '';
    const productItemId = selectedProduct?.item_id || null;
    
    // Calculate total: unit_price * quantity
    const unitPrice = selectedProduct?.unit_price || 0;
    const quantity = formData.quantity ? parseFloat(formData.quantity) : 0;
    const calculatedTotal = unitPrice * quantity;

    // Find selected section from sectionOptionsData
    let sectionObject = null;
    if (formData.section && sectionOptionsData.length > 0) {
      const sectionId = parseInt(formData.section, 10);
      const selectedSection = sectionOptionsData.find(section => {
        const sectionIdValue = section.id !== undefined ? section.id : section.value;
        return parseInt(sectionIdValue, 10) === sectionId;
      });
      
      if (selectedSection) {
        const sectionIdValue = selectedSection.id !== undefined ? selectedSection.id : selectedSection.value;
        const sectionValue = selectedSection.value !== undefined ? selectedSection.value : (selectedSection.label || selectedSection.name || String(sectionIdValue));
        sectionObject = {
          id: parseInt(sectionIdValue, 10),
          value: sectionValue
        };
      }
    }

    // Determine if this is an inquiry or task based on rowType prop
    const isInquiry = rowType === 'inquiry' || rowType === 'enquiry';
    const itemId = selectedRow?.item_id || selectedRow?.id || null;

    // Prepare API data in the required format
    const apiData = {
      product: productItemId ? parseInt(productItemId, 10) : null,
      Rum: formData.rum || '',
      product_title: productTitle,
      Section: sectionObject,
      Quantity: formData.quantity ? parseInt(formData.quantity, 10) : null,
      task_item_id: isInquiry ? null : itemId, // Only set if it's a task
      inquiry_item_id: isInquiry ? itemId : null, // Only set if it's an inquiry
      is_updated: isEditing,
      total: calculatedTotal
    };

    // Only include item_id when editing (updating existing product line)
    if (isEditing && editingProductLine.item_id) {
      apiData.item_id = editingProductLine.item_id;
    }

    // Console log the data in the same format
    console.log('Product Line Data:', JSON.stringify(apiData, null, 2));

    try {
      // Send data to API
      const response = await postWithToken('/api/create-or-update-product-line', {
        access_token: access_token,
        data: apiData
      });

      console.log('API Response:', response);
      
      if (response && response.status === 200 && response.data) {
        // Show success message
        showToast("Success", "Product line saved successfully!", {
          position: "top-center",
          autoClose: 3000,
        });

        // Update local state immediately with the new/updated product line
        const updatedProductLine = response.data;
        if (isEditing && editingProductLine) {
          // Update existing product line in the list
          setProductLines(prevLines => 
            prevLines.map(pl => 
              pl.id === editingProductLine.id || pl.item_id === editingProductLine.item_id
                ? {
                    ...pl,
                    name: productTitle,
                    title: productTitle,
                    section: sectionObject?.value || sectionObject?.id || formData.section,
                    price: unitPrice,
                    Quantity: quantity,
                    total: calculatedTotal,
                    ...updatedProductLine
                  }
                : pl
            )
          );
        } else {
          // Add new product line to the list
          const newProductLine = {
            id: updatedProductLine.id,
            item_id: updatedProductLine.item_id,
            name: productTitle,
            title: productTitle,
            section: sectionObject?.value || sectionObject?.id || formData.section,
            price: unitPrice,
            Quantity: quantity,
            total: calculatedTotal,
            ...updatedProductLine
          };
          setProductLines(prevLines => [...prevLines, newProductLine]);
        }

        // Close the form after successful submission
        handleCloseForm();
        
        // Call onUpdate callback to refresh data from server (ensures sync)
        if (onUpdate) {
          onUpdate();
        }
      } else {
        throw new Error('Unexpected response from API');
      }
      
    } catch (error) {
      console.error('Error updating product line:', error);
      showToast("Error", "Failed to save product line. Please try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  // Format value for display
  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'object' && !(value instanceof Date)) {
      return JSON.stringify(value);
    }
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }
    return String(value);
  };

  return (
    <div className="product-line-overlay" onClick={onOverlayClick}>
      <div className="product-line-overlay--content">
        <div className="divider-block">
          <button className="product-line-overlay--close" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18" stroke="#5F6A7C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M6 6L18 18" stroke="#5F6A7C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="product-line-overlay--header">
            <div className="overlaytitle">
              <h2>Product Line</h2>
            </div>
          </div>
          <div className="product-line-overlay-body">
           
           
            <div className="product-line-content">
              <div className="product-line-search-wrapper">
                <div className="product-line-search">
                  <input
                    type="text"
                    placeholder="Search Product line"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="product-line-search-input"
                  />
                  <svg 
                    className="product-line-search-icon"
                    xmlns="http://www.w3.org/2000/svg" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 16 16" 
                    fill="none"
                  >
                    <path 
                      d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" 
                      stroke="#5F6A7C" 
                      strokeWidth="1.33333" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                    <path 
                      d="M14 14L11.1 11.1" 
                      stroke="#5F6A7C" 
                      strokeWidth="1.33333" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <button 
                  className="product-line-add-button" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddClick();
                  }}
                >
                  ADD PRODUCT LINE
                </button>
              </div>
              <div className="product-line-edit-option-content-wrapper">
              {showEditForm && (
                <div className="product-line-edit-option-content">
                  <div className="edit-form-block">
                    <form>
                      <div 
                        className="close-edit-form"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseForm();
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M12 4L4 12" stroke="#5F6A7C" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M4 4L12 12" stroke="#5F6A7C" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="grouped-item">
                        <SelectField 
                          label="Product" 
                          value={formData.product} 
                          permission="edit" 
                          options={productOptions}
                          onChange={(e) => handleFieldChange('product', e.target.value)}
                          error={errors.product}
                        />
                        <TextField 
                          label="Rum" 
                          placeholder="Enter Rum" 
                          value={formData.rum} 
                          type="text" 
                          permission="edit" 
                          onChange={(e) => handleFieldChange('rum', e.target.value)}
                          error={errors.rum}
                        />
                        <SelectField 
                          label="Section" 
                          value={formData.section} 
                          permission="edit" 
                          options={sectionFieldOptions}
                          onChange={(e) => handleFieldChange('section', e.target.value)}
                          error={errors.section}
                        />
                        <TextField 
                          label="Quantity (Pcs. / Sqm)" 
                          placeholder="Enter Quantity" 
                          value={formData.quantity} 
                          type="text" 
                          permission="edit" 
                          onChange={(e) => handleFieldChange('quantity', e.target.value)}
                          error={errors.quantity}
                        />
                      </div>
                      <button 
                        type="button"
                        className="product-line-edit-option--button"
                        onClick={handleFormSubmit}
                      >
                        {editingProductLine ? 'Add' : 'Add'}
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </div>
              {filteredProductLines.length > 0 ? (
                <div className="product-line-table--data">
                  <div className="product-line-table--data--wrapper">
                    <div className="product-line-table--data--row product-line-table--data__header">
                      <div className="product-line--fixed-title-col">
                        <div className="product-line-table--data--cell">Title</div>
                      </div>
                      <div className="product-line--fixed-detail-col">
                        <div className="cell-data-fields">
                          <div className="product-line-table--data--cell">Section</div>
                          <div className="product-line-table--data--cell">Price</div>
                          <div className="product-line-table--data--cell">Stc/M2</div>
                          <div className="product-line-table--data--cell">Total</div>
                        </div>
                        <div className="cell-edit-field">
                          <div className="product-line-table--data--cell"></div>
                        </div>
                      </div>
                    </div>
                    {filteredProductLines.map((productLine, index) => (
                      <div key={productLine.id || index} className="product-line-table--data--row product-line-table--data__body">
                        <div className="product-line--fixed-title-col">
                          <div className="product-line-table--data--cell">{formatValue(productLine.name || productLine.title)}</div>
                        </div>
                        <div className="product-line--fixed-detail-col">
                          <div className="cell-data-fields">
                            <div className="product-line-table--data--cell">{formatValue(productLine.section)}</div>
                            <div className="product-line-table--data--cell">{formatValue(productLine.price)}</div>
                            <div className="product-line-table--data--cell">{formatValue(productLine.Quantity || productLine['Quantity'])}</div>
                            <div className="product-line-table--data--cell">{formatValue(productLine.total)}</div>
                          </div>
                          <div className="cell-edit-field">
                            <div className="product-line-table--data--cell">
                              <img 
                                src={editImage} 
                                alt="img" 
                                width="20" 
                                height="20"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(productLine);
                                }}
                                style={{ cursor: 'pointer' }}
                              ></img>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>No product lines available</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductLinePopup;

